import axios from "axios";
import api, { API } from "@/lib/api";

const MAX_SIDE = 2500;
const LIMIT = 5 * 1024 * 1024;
const DIRECT = 8 * 1024 * 1024;
const PART = 4 * 1024 * 1024;
const IMG = /\.(jpe?g|png|heic|heif|webp)$/i;

const isImage = (f) => /^image\/(jpeg|png|heic|heif|webp)$/i.test(f.type) || IMG.test(f.name || "");
const isHeic = (f) => /heic|heif/i.test(f.type) || /\.(heic|heif)$/i.test(f.name || "");
const jpgName = (n) => (n || "foto").replace(/\.[^.]+$/, "") + ".jpg";

async function bitmapOf(file) {
  try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* HEIC fora do Safari */ }
  if (!isHeic(file)) return null;
  const { default: heic2any } = await import("heic2any");
  const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  return createImageBitmap(Array.isArray(out) ? out[0] : out);
}

const toBlob = (canvas, q) => new Promise((r) => canvas.toBlob(r, "image/jpeg", q));

// Fotografias: lado maior até 2500 px, JPEG a 85%; se passar de 5 MB, baixa a qualidade por passos.
export async function shrinkImage(file) {
  if (!isImage(file)) return file;
  try {
    const bmp = await bitmapOf(file);
    if (!bmp) return file;
    const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    let q = 0.85, blob = await toBlob(c, q);
    while (blob && blob.size > LIMIT && q > 0.3) { q -= 0.1; blob = await toBlob(c, q); }
    return blob ? new File([blob], jpgName(file.name), { type: "image/jpeg" }) : file;
  } catch { return file; }
}

export const teamChunks = {
  start: (body) => api.post("/chunks/start", body),
  part: (id, i, blob) => api.put(`/chunks/${id}/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } }),
};

export const portalChunks = (token, session) => {
  const headers = { "X-Portal-Session": session };
  return {
    start: (body) => axios.post(`${API}/portal/${token}/chunks/start`, body, { headers }),
    part: (id, i, blob) => axios.put(`${API}/portal/${token}/chunks/${id}/${i}`, blob, { headers: { ...headers, "Content-Type": "application/octet-stream" } }),
  };
};

async function sendInParts(file, ch) {
  const { data } = await ch.start({ name: file.name || "ficheiro", size: file.size });
  for (let i = 0, n = 0; i < file.size; i += PART, n += 1) await ch.part(data.id, n, file.slice(i, i + PART));
  return data.id;
}

// Formulário pronto a enviar: fotos reduzidas no dispositivo; ficheiros grandes vão antes, em partes (campo «<nome>_ref»).
export async function makeForm(entries, ch = teamChunks) {
  const fd = new FormData();
  for (const [name, value] of entries) {
    if (!(value instanceof Blob)) { fd.append(name, value); continue; }
    const f = await shrinkImage(value);
    if (f.size > DIRECT) fd.append(`${name}_ref`, await sendInParts(f, ch));
    else fd.append(name, f, f.name);
  }
  return fd;
}
